import React, { useState } from "react";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

/**
 * Generic CriteriaMatrix — renders ANY ProposalKind's matrix.
 *
 * Reads only the matrix.criteria + matrix.cells + matrix.subject_scores shape
 * delivered by the backend. NO hardcoded labels, NO hardcoded document-type
 * logic. Adding a new ProposalKind requires zero changes to this component.
 */
export default function CriteriaMatrix({ matrix, subjectsById = {}, trade_offs = [] }) {
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [expandedCell, setExpandedCell] = useState(null);
  if (!matrix?.criteria?.length) return null;

  const criteria = criticalOnly
    ? matrix.criteria.filter((c) => c.weight_tier === "critical")
    : matrix.criteria;
  const subjectScores = matrix.subject_scores || [];
  const subjectsInOrder = subjectScores.map((s) => s.doc_id);
  const cellLookup = {};
  for (const c of matrix.cells || []) {
    cellLookup[`${c.doc_id}::${c.criterion_key}`] = c;
  }

  const tradeOffKeys = new Set(trade_offs.map((t) => t.criterion_key));

  return (
    <section data-testid="criteria-matrix" className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Info size={14} className="text-zinc-700" strokeWidth={1.75} />
          <h3 className="font-display font-semibold text-base tracking-tight">Criteria matrix</h3>
          <span className="text-xs text-zinc-500">({criteria.length} of {matrix.criteria.length})</span>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={criticalOnly}
            onChange={(e) => setCriticalOnly(e.target.checked)}
            data-testid="criteria-matrix-critical-toggle"
            className="rounded border-zinc-300"
          />
          Critical criteria only
        </label>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-xl bg-white">
        <table className="w-full text-sm" data-testid="criteria-matrix-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-zinc-700 sticky left-0 bg-zinc-50 z-10 min-w-[180px]">
                Criterion
              </th>
              {subjectsInOrder.map((did) => {
                const score = subjectScores.find((s) => s.doc_id === did);
                const subject = subjectsById[did] || { filename: did.slice(0, 8) };
                const isWinner = score?.rank === 1;
                return (
                  <th
                    key={did}
                    data-testid={`criteria-matrix-column-${did}`}
                    className={`text-left px-3 py-2 font-semibold min-w-[160px] ${
                      isWinner ? "bg-emerald-50 text-emerald-900" : "text-zinc-700"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider tabular-nums">#{score?.rank}</span>
                      <span className="truncate" title={subject.filename}>{subject.filename}</span>
                    </div>
                    <div className="text-[10px] text-zinc-500 font-normal mt-0.5">
                      Score: <span className="font-semibold tabular-nums text-zinc-700">{((score?.weighted_score ?? 0) * 100).toFixed(1)}</span>
                      {score?.tied_with?.length > 0 && (
                        <span className="ml-1.5 inline-flex items-center gap-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] uppercase tracking-wider"
                              data-testid={`criteria-matrix-tie-${did}`}>
                          Stat. tie
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {criteria.map((c) => {
              const expanded = expandedCell === c.key;
              const isTradeOff = tradeOffKeys.has(c.key);
              return (
                <React.Fragment key={c.key}>
                  <tr
                    className={`hover:bg-zinc-50 transition-colors cursor-pointer ${isTradeOff ? "ring-1 ring-amber-200" : ""}`}
                    onClick={() => setExpandedCell(expanded ? null : c.key)}
                    data-testid={`criteria-matrix-row-${c.key}`}
                  >
                    <td className="px-3 py-2 sticky left-0 bg-white z-10">
                      <div className="flex items-start gap-1.5">
                        {expanded ? <ChevronUp size={12} className="mt-0.5 text-zinc-400" /> : <ChevronDown size={12} className="mt-0.5 text-zinc-400" />}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-zinc-900">{c.label}</span>
                            {c.weight_tier === "critical" && (
                              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 font-semibold">Critical</span>
                            )}
                            {c.weight_tier === "important" && (
                              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-semibold">Important</span>
                            )}
                            {isTradeOff && (
                              <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold">Trade-off</span>
                            )}
                          </div>
                          {c.unit && <span className="text-[10px] text-zinc-400">{c.unit}</span>}
                        </div>
                      </div>
                    </td>
                    {subjectsInOrder.map((did) => {
                      const cell = cellLookup[`${did}::${c.key}`];
                      return (
                        <td
                          key={did}
                          data-testid={`criteria-matrix-cell-${c.key}-${did}`}
                          className={`px-3 py-2 ${tone(cell, c)}`}
                        >
                          {cell ? (
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="font-mono text-xs tabular-nums">{cell.display_value}</span>
                              {cell.normalized_score != null && (
                                <span className="text-[10px] text-zinc-500 tabular-nums">
                                  {(cell.normalized_score * 100).toFixed(0)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-400 italic">missing</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {expanded && (
                    <tr data-testid={`criteria-matrix-expanded-${c.key}`}>
                      <td colSpan={subjectsInOrder.length + 1} className="px-3 py-2 bg-zinc-50 text-xs text-zinc-700">
                        <div className="space-y-1.5">
                          <p>{c.description}</p>
                          <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-zinc-500">
                            <span>Direction: <span className="text-zinc-700 font-semibold">{c.direction.replace("_", " ")}</span></span>
                            <span>Weight: <span className="text-zinc-700 font-semibold">{c.weight_tier}</span></span>
                            <span>Value type: <span className="text-zinc-700 font-semibold">{c.value_type}</span></span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Trade-offs */}
      {trade_offs.length > 0 && (
        <div data-testid="criteria-matrix-trade-offs" className="space-y-1.5">
          <h4 className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Key trade-offs</h4>
          <ul className="space-y-1.5">
            {trade_offs.map((t, i) => {
              const wname = subjectsById[t.winner_doc_id]?.filename || t.winner_doc_id.slice(0, 8);
              const lname = subjectsById[t.loser_doc_id]?.filename || t.loser_doc_id.slice(0, 8);
              const tone =
                t.magnitude === "material" ? "bg-rose-50 border-rose-200 text-rose-900"
                  : t.magnitude === "significant" ? "bg-amber-50 border-amber-200 text-amber-900"
                  : "bg-zinc-50 border-zinc-200 text-zinc-700";
              return (
                <li key={i} className={`text-sm px-3 py-2 border rounded-lg ${tone}`} data-testid={`trade-off-${i}`}>
                  <span className="font-semibold">{wname}</span>{" "}beats{" "}
                  <span className="font-semibold">{lname}</span>{" "}on{" "}
                  <span className="italic">{matrix.criteria.find((c) => c.key === t.criterion_key)?.label || t.criterion_key}</span>{" "}
                  — <span className="font-mono text-xs">{t.delta}</span>
                  <span className="ml-2 text-[9px] uppercase tracking-wider opacity-70">{t.magnitude}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function tone(cell, criterion) {
  if (!cell || cell.normalized_score == null) return "";
  const s = cell.normalized_score;
  if (criterion.direction === "qualitative") return "";
  if (s >= 0.95) return "bg-emerald-50";
  if (s >= 0.6) return "bg-emerald-50/40";
  if (s <= 0.05) return "bg-rose-50";
  if (s <= 0.3) return "bg-rose-50/40";
  return "";
}
